import {useIsRTL} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import React, {forwardRef, SyntheticEvent, useMemo} from 'react';
import sc, {css, keyframes} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {InfoCircleWithDialog} from '../../organisms/InfoCircle/InfoCircleWithDialog';
import {CountdownBoxed} from '../Countdown/CountdownBoxed';

export interface DiscountCardProps {
  headline: string;
  subline?: string;
  validUntil?: Date;
  backgroundColor: string;
  conditionText?: string;
  savingsLabel?: string;
  showCountdown?: boolean;
  active?: boolean;
  withBorder?: boolean;
  onClick?: VoidFunction;
  className?: string;
  'data-testid'?: string;
  DiscountCardThumbnails?: React.ReactElement;
  BackgroundIcon?: React.ReactElement;
}

const getHeadlineFontSize = (headline: string) => {
  const baseSize = 16;
  // this essentially is the number of how much the font size decreases by new letter
  const letterFactor = 0.1;

  // thats the length where we start decreasing the font size
  const maxHeadlineLength = 45;

  if (headline.length > maxHeadlineLength) {
    const textLength = headline.length - maxHeadlineLength;
    // make the decreasing a bit more graceful
    const textSize = baseSize - textLength * letterFactor;
    return textSize <= 13 ? 13 : textSize;
  }

  return baseSize;
};

export const DiscountCard = forwardRef<HTMLButtonElement, DiscountCardProps>(
  function DiscountCard(props, ref) {
    const {
      headline,
      subline,
      validUntil,
      onClick,
      backgroundColor,
      conditionText,
      active = true,
      withBorder,
      className,
      savingsLabel,
      DiscountCardThumbnails,
      showCountdown,
      BackgroundIcon,
      'data-testid': dataTestid,
      ...rest
    } = props;

    const isRTL = useIsRTL();
    /**
     * Only used for desktop - mobile doesnt need it as we dont have unselected there
     */

    const headlineFontSize = useMemo(() => {
      const result = getHeadlineFontSize(headline);
      return result;
    }, [headline, subline]);

    const savingFontSize = useMemo(() => {
      const result = getHeadlineFontSize(savingsLabel || '');
      return result;
    }, [savingsLabel, subline]);

    const handleOnClick = (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.();
    };

    return (
      <LinkAndInfoIconWrapper className={className} data-testid={dataTestid}>
        <Wrapper
          $backgroundColor={backgroundColor}
          onClick={handleOnClick}
          data-active={active}
          ref={ref}
          data-with-border={withBorder}
          aria-label={undefined}
          {...rest}
        >
          {BackgroundIcon ? (
            <>{BackgroundIcon}</>
          ) : (
            <SaleIcon
              asset={
                isRTL ? GenericIconName.SaleCloudRTL : GenericIconName.SaleCloud
              }
            />
          )}
          {validUntil && showCountdown && (
            <StyledCountdownBoxed
              endDate={validUntil}
              size="small"
              backgroundColor="rgba(0,0,0,0.1)"
              fontColor={!active ? 'inherit' : undefined}
              light
              $fill={!active ? 'rgba(0,0,0,0.5)' : COLORS.White}
              $animateFadeOut={Boolean(DiscountCardThumbnails)}
            />
          )}
          <TextWrapper>
            <HeadlineWrapper
              $hasSubline={Boolean(subline)}
              $hasCountdown={showCountdown}
              $animateMoveUp={Boolean(DiscountCardThumbnails)}
            >
              <Subline
                $animated={!!savingsLabel}
                $animateFadeOut={Boolean(DiscountCardThumbnails)}
                $hasSubline={Boolean(subline)}
              >
                {subline}
              </Subline>
              <Headline
                $animated={!!savingsLabel}
                $fontSize={headlineFontSize}
                $hasCountdown={showCountdown}
                $hasSubline={Boolean(subline)}
              >
                {headline}
              </Headline>
            </HeadlineWrapper>
            {DiscountCardThumbnails}
            {savingsLabel && (
              <Savings $hasSubline={false} $animated $fontSize={savingFontSize}>
                {savingsLabel}
              </Savings>
            )}
          </TextWrapper>
        </Wrapper>
        <>
          {/* we can control text colour by making it inherit -> its solving problem of changing colour on hover */}
          {conditionText && (
            <StyledInfoCircleWithDialog
              header={headline}
              conditions={conditionText}
              fill={!active ? 'inherit' : 'white'}
            />
          )}
        </>
      </LinkAndInfoIconWrapper>
    );
  },
);

const LinkAndInfoIconWrapper = linariaStyled.div`
  position: relative;
`;

const SaleIcon = linariaStyled(Icon)`
  position: absolute;
  top: -50px;
  left: -45px;
  width: 100px;
  height: 100px;
  opacity: 0.15;
`;

const TextWrapper = sc.div`
  display: grid;
  position: relative;
  width: 100%;
  height: 100%;

  justify-content: center;
  align-items: center;
  text-align: center;
`;

const moveVertically = ({
  translateY,
}: {
  translateY: '-20px' | '-30px' | '-40px';
}) => keyframes`
  from {
    transform: translateY(0px);
  }
  to {
    transform: translateY(${translateY});
  }
`;

const HeadlineWrapper = sc.div<{
  $hasCountdown?: boolean;
  $animateMoveUp?: boolean;
  $hasSubline?: boolean;
}>`
  grid-area: 1/1/2/2;
  padding-inline: 7px;

  ${({$animateMoveUp, $hasCountdown, $hasSubline}) => {
    if (!$animateMoveUp) {
      return '';
    }
    let animation = moveVertically({translateY: '-20px'});
    if ($hasCountdown && $hasSubline) {
      animation = moveVertically({translateY: '-40px'});
    } else if ($hasCountdown || $hasSubline) {
      animation = moveVertically({translateY: '-30px'});
    }
    return css`
      animation: ${animation} 0.5s;
      animation-fill-mode: forwards;
    `;
  }}
`;

const fade = css`
  @keyframes fade {
    0% {
      opacity: 1;
    }
    45% {
      opacity: 1;
    }
    55% {
      opacity: 0;
    }
    100% {
      opacity: 0;
    }
  }

  animation: fade 2s ease infinite alternate;
`;

const fadeOut = css`
  @keyframes fadeOut {
    0% {
      opacity: 1;
    }
    99% {
      opacity: 0.01;
    }
    100% {
      opacity: 0;
    }
  }

  animation: fadeOut 0.5s;
  animation-fill-mode: forwards;
`;

const Headline = sc.div<{
  $fontSize: number;
  $animated: boolean;
  $animateMoveUp?: boolean;
  $hasSubline?: boolean;
  $hasCountdown?: boolean;
}>`
  font-size: ${({$fontSize}) => $fontSize}px;
  font-weight: ${FONT_WEIGHTS.heavy};
  display: flex;
  flex-direction: column;
  color: inherit;

  ${({$animated}) => $animated && fade}
`;

const Subline = sc.div<{
  $animated: boolean;
  $animateFadeOut?: boolean;
  $hasSubline?: boolean;
}>`
  font-size: 0.625rem;
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  margin-bottom: 4px;
  color: inherit;

  ${({$hasSubline}) => !$hasSubline && 'margin-bottom: 0px;'}
  ${({$animated}) => $animated && fade}
  ${({$animateFadeOut}) => $animateFadeOut && fadeOut}
`;

const Savings = sc(Headline)<{$hasSubline?: boolean}>`
  grid-area: 1/1/2/2;
  display: flex;
  align-items: center;
  z-index: 1;
  opacity: 0;
  animation-direction: alternate-reverse;
`;

const StyledInfoCircleWithDialog = sc(InfoCircleWithDialog)`
  &&&& {
    top: 9px;
    right: 9px;
  }
`;

const StyledCountdownBoxed = sc(CountdownBoxed)<{
  $fill: string;
  $animateFadeOut?: boolean;
}>`
  & svg {
    fill: ${({$fill}) => $fill};
  }
  ${({$animateFadeOut}) => $animateFadeOut && fadeOut};
`;

export const Wrapper = sc(UnstyledButton)<{
  $backgroundColor?: string;
}>`
  && {
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: ${COLORS.White};

    padding: 6px 20px 10px;
    &:hover svg {
      fill: white;
    }

    /**
      This is needed for safari because otherwise the overflow hidden doesnt
      work if you apply a transform to the card
      https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b
    */
    -webkit-mask-image: -webkit-radial-gradient(white, black);
    
    box-sizing: border-box;
    border-radius: 5px;
    height: 100px;
    padding: 6px 20px 10px;
    width: 100%;
    color: rgba(0,0,0,0.5);


    &:hover {
      cursor: pointer;
      svg {
        fill: ${COLORS.White};
      }

      color: ${COLORS.White};
      background-color: ${props => props.$backgroundColor};
    }
    
    &[data-with-border="true"] {
      border: 1px solid rgba(0,0,0,0.1);
    }
    
    &[data-active="true"] {
      color: ${COLORS.White};
      background-color: ${props => props.$backgroundColor};
      
      &:hover {
        color: ${COLORS.White};
      }
    }

    &:focus-visible {
      ${tabFocusedStyles({
        offset: '-4px',
      })}
    }
  }

`;
